import { useState, useEffect } from "react";

// {{char:...}} / {{keyword}} などのマークアップタグをスキップして
// 可視文字数を基準に slicePoint を返す。タグの途中でカットしない。
// ストリーミング中に {{ で始まる不完全なタグに当たった場合は、そこで打ち切る。
function findSlicePoint(text: string, visibleCount: number): number {
  let visible = 0;
  let i = 0;
  while (i < text.length) {
    if (text[i] === "{" && i + 1 < text.length && text[i + 1] === "{") {
      const end = text.indexOf("}}", i + 2);
      if (end !== -1) {
        i = end + 2; // タグ全体をスキップ（可視カウントは増やさない）
        continue;
      }
      // ストリーミング中の不完全タグ: 生テキストを見せないよう打ち切る
      break;
    }
    if (visible >= visibleCount) break;
    visible++;
    i++;
  }
  return i;
}

function countVisible(text: string): number {
  // 完全なタグを除去し、不完全なタグ（}}未着信）以降も除外してカウント
  return text.replace(/\{\{[^}]+\}\}/g, "").replace(/\{\{.*$/, "").length;
}

// 可視文字を 1 文字ずつ rAF で出現させる。
// prefers-reduced-motion が reduce のときは即時全表示。
export function useSegmentTypewriter(text: string, durationMs: number): string {
  const prefersReduced =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const visibleTotal = countVisible(text);
  const [visibleCount, setVisibleCount] = useState(
    prefersReduced ? visibleTotal : 0,
  );

  useEffect(() => {
    if (prefersReduced) {
      setVisibleCount(visibleTotal);
      return;
    }
    setVisibleCount(0);
    if (visibleTotal === 0) return;

    let cancelled = false;
    let startTime: number | null = null;

    const tick = (ts: number) => {
      if (cancelled) return;
      if (startTime === null) startTime = ts;
      const elapsed = ts - startTime;
      const next = Math.min(
        Math.floor((elapsed / durationMs) * visibleTotal),
        visibleTotal,
      );
      setVisibleCount(next);
      if (next < visibleTotal) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
    return () => {
      cancelled = true;
    };
  }, [text, durationMs, visibleTotal, prefersReduced]);

  if (prefersReduced) return text;
  const slicePoint = findSlicePoint(text, visibleCount);
  return text.slice(0, slicePoint);
}
